/* eslint-disable react/no-array-index-key */
/**
 * @fileoverview Componente molecule TemplateMessagePreview refactorizado con separación de responsabilidades.
 * Utiliza arquitectura de hooks en capas para optimizar performance y mantenibilidad.
 * Implementa patrón Data/Form/Actions/UI Layer según arquitectura Mercately.
 *
 * @module TemplateMessagePreview
 * @category Component - Molecule
 * @since 1.0.0
 * @version 2.0.0 - Refactorización completa con separación de capas y optimización de renders
 */
import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import PreviewFile from '../../../components/PreviewFile';
import useTemplateText from '../hooks/useTemplateText';
import useTemplateMessageForm from '../../../hooks/newchat/forms/useTemplateMessageForm';
import useTemplateMessageActions from '../../../hooks/newchat/actions/useTemplateMessageActions';
import useTemplateMessageUI from '../../../hooks/newchat/ui/useTemplateMessageUI';

/**
 * Hook composition pattern para el componente TemplateMessagePreview.
 *
 * Implementa separación estricta de responsabilidades siguiendo la arquitectura
 * de capas de Mercately para optimizar performance y mantenibilidad.
 */
const TemplateMessagePreview = memo(({
  selectedTemplate,
  dynamicFieldNames,
  carouselData,
  className = ''
}) => {
  const { t } = useTranslation('NewChat');
  const { setValue, control } = useFormContext();

  // Hook original para mantener compatibilidad
  const calculateTemplateText = useTemplateText(selectedTemplate);

  /*
   * LAYER 2: FORM LAYER
   * Responsabilidad: Observación de formulario y campos
   */
  const {
    dynamicFieldValues,
    attachedFile,
    carouselFiles,
    carouselValuesMap,
    updateFormMessage
  } = useTemplateMessageForm(
    control,
    setValue,
    dynamicFieldNames,
    carouselData
  );

  /*
   * LAYER 3: ACTIONS LAYER
   * Responsabilidad: Lógica de negocio y procesamiento
   */
  const {
    processedTemplateText,
    ctaButtons,
    carouselProcessedData
  } = useTemplateMessageActions(
    selectedTemplate,
    calculateTemplateText,
    dynamicFieldNames,
    dynamicFieldValues,
    carouselValuesMap,
    carouselData
  );

  /*
   * LAYER 4: UI LAYER
   * Responsabilidad: Estados de renderizado y configuración de UI
   */
  const {
    shouldRenderPreview,
    shouldShowCTAButtons,
    shouldShowCarousel,
    renderConfig
  } = useTemplateMessageUI(selectedTemplate, ctaButtons, className);

  /**
   * Efecto para sincronización con formulario principal.
   *
   * Actualiza el campo 'message' del formulario cuando cambia
   * el texto procesado de la plantilla.
   */
  useEffect(() => {
    if (processedTemplateText) {
      updateFormMessage(processedTemplateText);
    }
  }, [processedTemplateText, updateFormMessage]);

  /**
   * Función para renderizar botones CTA de manera optimizada.
   *
   * Renderiza botones Call-to-Action extraídos del texto de plantilla
   * con optimización para evitar renders innecesarios.
   */
  const renderCtaButtons = () => {
    if (!shouldShowCTAButtons || ctaButtons.length === 0) return null;

    return (
      <div className="tw-space-y-1">
        {ctaButtons.map((btn, index) => (
          <div key={`cta-button-${index}-${btn}`} className="tw-flex tw-w-full tw-px-0">
            <button
              type="button"
              className="tw-w-full tw-px-0 tw-bg-blue-500 tw-text-white tw-py-2 tw-rounded"
              aria-label={`${t('apiMessage.ctaButton')}: ${btn}`}
            >
              <span className="tw-font-medium">{btn}</span>
            </button>
          </div>
        ))}
      </div>
    );
  };

  /**
   * Función para renderizar card individual del carousel con datos procesados.
   *
   * Renderiza una card específica del carousel con texto procesado,
   * archivos adjuntos y botones correspondientes.
   */
  const renderCarouselCard = (cardData, cardIndex) => {
    // Obtener archivo para esta card
    const cardFile = Array.isArray(carouselFiles) && carouselFiles[cardIndex]?.length > 0
      ? carouselFiles[cardIndex][0]
      : null;
    const isVideoHeader = cardData.headerType?.toLowerCase() === 'video';
    let mediaPreview = <i className="mi-image-outline tw-text-gray-500 tw-text-3xl" />;

    if (cardFile && isVideoHeader) {
      mediaPreview = (
        <video
          src={URL.createObjectURL(cardFile)}
          className="tw-w-full tw-h-full tw-object-cover"
          muted
          controls={false}
        />
      );
    } else if (cardFile) {
      mediaPreview = (
        <img
          src={URL.createObjectURL(cardFile)}
          className="tw-w-full tw-h-full tw-object-cover"
          alt={`Card ${cardIndex + 1}`}
        />
      );
    } else if (isVideoHeader) {
      mediaPreview = <i className="mi-video-line tw-text-gray-500 tw-text-3xl" />;
    }

    return (
      <div
        key={cardIndex}
        className="tw-bg-white tw-rounded-lg tw-min-w-[160px] tw-w-[160px] tw-snap-start tw-text-gray-800 tw-text-xs tw-leading-5 tw-overflow-hidden tw-shadow-sm tw-border tw-border-gray-200 tw-flex-shrink-0"
      >
        <div className="tw-h-[100px] tw-bg-gray-200 tw-flex tw-items-center tw-justify-center tw-overflow-hidden">
          {mediaPreview}
        </div>

        <div className="tw-p-2">
          <div className="tw-text-gray-800 tw-text-xs tw-whitespace-pre-line tw-mb-2 tw-break-words">
            {cardData.processedBody || cardData.description}
          </div>

          {cardData.buttons && cardData.buttons.map((btn, btnIndex) => (
            <div key={btnIndex} className="tw-mt-1">
              <div className="tw-w-full tw-py-1 tw-px-2 tw-bg-blue-500 tw-text-white tw-text-xs tw-font-medium tw-rounded tw-text-center">
                <div>{btn.text}</div>
                {btn.url && (
                  <p
                    style={{
                      wordBreak: 'break-all',
                      whiteSpace: 'pre-wrap',
                      marginTop: 4
                    }}
                  >
                    {btn.url}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  /**
   * Validación defensiva: no renderiza si no hay plantilla válida.
   */
  if (!shouldRenderPreview) {
    return null;
  }

  /**
   * Renderizado principal del componente optimizado.
   */
  return (
    <div className={renderConfig.wrapperClass}>
      <div className="tw-space-y-4">
        <h4 className="tw-text-base tw-font-medium tw-text-gray-900">
          {t('apiMessage.messageExample')}
        </h4>

        <div className="tw-flex tw-justify-center">
          {shouldShowCarousel ? (
            /* Renderizado para carousel con datos procesados */
            <div className="tw-bg-green-100 tw-rounded-lg tw-max-w-sm tw-text-gray-800 tw-text-sm tw-leading-6 tw-break-words tw-whitespace-pre-wrap">
              {processedTemplateText && (
                <div className="tw-p-3 tw-pb-0">
                  <div className="tw-mb-3">
                    {processedTemplateText}
                  </div>
                </div>
              )}

              <div className="tw-p-3 tw-pt-0">
                <div className="tw-flex tw-flex-row tw-overflow-x-auto tw-gap-2 tw-pb-2 tw-snap-x">
                  {carouselProcessedData.map(renderCarouselCard)}
                </div>
              </div>
            </div>
          ) : (
            /* Renderizado para templates normales */
            <div className="tw-bg-green-100 tw-rounded-lg tw-max-w-sm tw-text-gray-800 tw-text-sm tw-leading-6 tw-break-words tw-whitespace-pre-wrap">
              {attachedFile?.length > 0 && (
                <div className="tw-pt-1 tw-px-1 tw-flex tw-justify-center tw-items-center">
                  <PreviewFile selectedFile={attachedFile[0]} />
                </div>
              )}

              <div className="tw-p-3">
                {processedTemplateText && (
                  <div className="tw-mb-2">
                    {processedTemplateText}
                  </div>
                )}
                {renderCtaButtons()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

/**
 * Nombre de display para el componente refactorizado.
 */
TemplateMessagePreview.displayName = 'TemplateMessagePreview';

export default TemplateMessagePreview;
